<script>
import axios from '@/utils/axios';
import { clientAdmin } from "@/utils/auth";

export default {
  inject: ['setLoading'],
  data() {
    return {
      errors: {},
      title: "HRM",
      text: "Asset",
      text1: "Assets",
      clientAdmin: clientAdmin(),
      filter: {
        category_id: '',
        status: '',
        search: '',
        page: 1,
      },
      statuses: [
        { id: 'available', name: 'Available' },
        { id: 'assigned', name: 'Assigned' },
        { id: 'maintenance', name: 'Maintenance' },
        { id: 'retired', name: 'Retired' },
      ],
      data: [],
      pagination: {},
      noDataMessage: '',
      categories: [],
      employees: [],
      selectedAsset: null,
    };
  },

  mounted() {
    this.getDropdowns();
    this.fetchAssets();
  },

  methods: {

    async fetchAssets(resetPage = true) {
      this.errors = {};
      if (resetPage) this.filter.page = 1;
      this.setLoading(true);
      try {
        const query_string = new URLSearchParams(this.filter).toString();
        const response = await axios.get(`/hrms/assets?${query_string}`);

        if (!response.data.status) {
          this.data = [];
          this.pagination = {};
          this.noDataMessage = response.data.message || 'No records found.';
          return;
        }

        this.noDataMessage = '';
        const rows = response.data.data || {};
        this.data = rows.data || [];
        this.pagination = rows;
      } catch (error) {
        this.data = [];
        this.pagination = {};
        this.noDataMessage = 'No records found.';
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    goToPage(page) {
      if (!page || page === this.pagination.current_page) return;
      this.filter.page = page;
      this.fetchAssets(false);
    },

    async getDropdowns() {
      try {
        const [catRes, empRes] = await Promise.allSettled([
          axios.get('/hrms/assets/categories'),
          axios.get('/hrms/employees', { params: { per_page: 1000 } }),
        ]);
        this.categories = catRes.status === 'fulfilled' ? (catRes.value.data.data || []) : [];
        this.employees = empRes.status === 'fulfilled' ? (empRes.value.data.data.data || []) : [];
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    hasActiveFilter() {
      return this.filter.category_id || this.filter.status || this.filter.search;
    },

    clearFilter() {
      this.filter.category_id = '';
      this.filter.status = '';
      this.filter.search = '';
      this.fetchAssets();
    },

    openCreateModal() {
      this.selectedAsset = null;
      this.$nextTick(() => this.$openModal('hrm_asset_modal'));
    },

    openEditModal(asset) {
      this.selectedAsset = asset;
      this.$nextTick(() => this.$openModal('hrm_asset_modal'));
    },

    openAssignModal(asset) {
      this.selectedAsset = asset;
      this.$nextTick(() => this.$openModal('hrm_asset_assign_modal'));
    },

    async deleteAsset(asset) {
      const result = await this.$confirm({ text: `Delete "${asset.name}"? This cannot be undone.` });
      if (!result.isConfirmed) return;
      this.setLoading(true);
      try {
        await axios.delete(`/hrms/assets/${asset.id}`);
        this.$swalToast('Asset deleted successfully.');
        this.fetchAssets(false);
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },

    availableQty(asset) {
      return (asset.quantity || 0) - (asset.assigned_quantity || 0);
    },

    statusClass(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'available') return 'bg-success';
      if (s === 'assigned') return 'bg-info';
      if (s === 'maintenance') return 'bg-warning';
      if (s === 'retired') return 'bg-secondary';
      return 'bg-light text-dark';
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap asset-head-actions">
            <router-link to="/hrms/assets/categories" class="btn btn-outline-secondary d-flex align-items-center me-2">
                <i class="ti ti-category me-2"></i>Categories
            </router-link>
            <router-link to="/hrms/assets/assignments" class="btn btn-outline-secondary d-flex align-items-center me-2">
                <i class="ti ti-history me-2"></i>Assignment History
            </router-link>
            <button class="btn btn-primary d-flex align-items-center" @click="openCreateModal">
                <i class="ti ti-plus me-2"></i>Add Asset
            </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <input type="text" class="form-control" placeholder="Search by name, code, serial..."
                v-model="filter.search" @keyup.enter="fetchAssets()" style="min-width: 220px;">
            </div>
            <div class="me-3">
              <vue-select v-model="filter.category_id" :options="categories" label="name" :reduce="c => c.id"
                placeholder="Category" :clearable="true" @update:modelValue="fetchAssets()" style="min-width: 180px;"></vue-select>
            </div>
            <div class="me-3">
              <vue-select v-model="filter.status" :options="statuses" label="name" :reduce="s => s.id"
                placeholder="Status" :clearable="true" @update:modelValue="fetchAssets()" style="min-width: 160px;"></vue-select>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                    <tr>
                    <th>#</th>
                    <th>Asset</th>
                    <th>Category</th>
                    <th>Brand / Model</th>
                    <th>Serial No.</th>
                    <th>Available</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id || index">
                        <td>{{ (pagination.per_page * (pagination.current_page - 1)) + index + 1 }}.</td>
                        <td>
                            <div class="d-flex align-items-center gap-2">
                              <div>
                                <strong class="text-dark">{{ row.name || '-' }}</strong>
                                <small class="text-muted d-block" style="font-size:11px;">{{ row.code || '' }}</small>
                              </div>
                              <span class="badge border text-primary" style="font-size:10px;" v-if="row.is_trackable">
                                <i class="ti ti-barcode me-1"></i>Trackable
                              </span>
                            </div>
                        </td>
                        <td>{{ row.category?.name || '-' }}</td>
                        <td>
                          {{ row.brand || '-' }}
                          <small class="text-muted d-block" style="font-size:11px;">{{ row.model || '' }}</small>
                        </td>
                        <td>{{ row.serial_number || '-' }}</td>
                        <td>{{ availableQty(row) }} / {{ row.quantity }}</td>
                        <td>
                            <span class="badge rounded-pill" :class="statusClass(row.status)">
                            {{ row.status }}
                            </span>
                        </td>
                        <td class="text-end">
                          <button class="btn btn-sm btn-outline-primary me-1" :disabled="row.status !== 'available' || availableQty(row) <= 0"
                            @click="openAssignModal(row)">
                            <i class="ti ti-user-plus me-1"></i>Assign
                          </button>
                          <button class="btn btn-sm btn-outline-secondary me-1" @click="openEditModal(row)">
                            <i class="ti ti-edit"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="deleteAsset(row)">
                            <i class="ti ti-trash"></i>
                          </button>
                        </td>
                        </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">{{ noDataMessage || 'No records found.' }}</div>

              <!-- Pagination -->
              <div class="d-flex align-items-center justify-content-between p-3" v-if="pagination.last_page > 1">
                <span class="text-muted" style="font-size:12px;">
                  Showing {{ pagination.from || 0 }} to {{ pagination.to || 0 }} of {{ pagination.total || 0 }}
                </span>
                <nav>
                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(pagination.current_page - 1)">Previous</a>
                    </li>
                    <li class="page-item" v-for="p in pagination.last_page" :key="p" :class="{ active: p === pagination.current_page }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(p)">{{ p }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                      <a class="page-link" href="javascript:void(0);" @click="goToPage(pagination.current_page + 1)">Next</a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <hrm-asset-modal :asset="selectedAsset" :categories="categories" @saved="fetchAssets(false)" />
  <hrm-asset-assign-modal :asset="selectedAsset" :employees="employees" @assigned="fetchAssets(false)" />
</template>

<style scoped>
@media (max-width: 767px) {
  .page-breadcrumb { flex-direction: column; align-items: stretch !important; gap: 10px; }

  .asset-head-actions {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    width: 100%;
    margin: 0 !important;
  }
  .asset-head-actions .btn {
    width: 100%;
    justify-content: center;
    font-size: 12.5px;
    padding: 9px 8px;
    margin: 0 !important;
  }
  .asset-head-actions .btn-primary {
    grid-column: 1 / -1;
  }

  .card-body.d-flex.align-items-center.justify-content-center.flex-wrap {
    justify-content: flex-start !important;
  }
  .card-body.d-flex.align-items-center.justify-content-center.flex-wrap .d-flex.align-items-center.flex-wrap.mb-1.gap-2 {
    flex-direction: column;
    align-items: stretch !important;
    width: 100%;
  }
  .card-body.d-flex.align-items-center.justify-content-center.flex-wrap .me-3 {
    margin-right: 0 !important;
    width: 100%;
  }
  .card-body.d-flex.align-items-center.justify-content-center.flex-wrap input,
  .card-body.d-flex.align-items-center.justify-content-center.flex-wrap .v-select {
    min-width: 100% !important;
    width: 100%;
  }
}
</style>